import {
  parseNotesString,
  parseStorageLocationMeta,
  type ParsedNotesData,
  type SavedSpecimenRef,
} from "../components/frozen-storage-data";
import { CONSUMING_SPECIMEN_USAGES } from "./frozenSpecimenUsages";
import type { Specimen, StorageLocation } from "./useSpecimens";

/**
 * What a saved specimen can tell the intake that reopens on it.
 *
 * The oocyte intake is an *add* form prefilled from the cycle's step data, and
 * three of the things it asks for do not live on the cycle at all:
 *
 * - the retrieval date is on the specimen as `collectedAt`
 * - the freeze date is the active frozen storage location's `storedFrom`
 * - the facility, the tank reference and the location's city/country/type are
 *   on that same storage location
 *
 * So reopening the form showed them blank however many times she had filled
 * them in. The freeze date in particular could never come back through the
 * cycle: the `freezing` step shares the retrieval phase and
 * `deriveStepProgress` maps a phase date onto `egg_retrieval` but not onto
 * `freezing`, so `freezing.freezeDate` is always absent. Reading the specimen
 * instead avoids teaching that shared presenter a new field — it is consumed by
 * the hub, the homepage and the milestones, and the comment in it records what
 * an earlier ill-weighed addition there cost.
 */
export type SpecimenIntakePrefill = {
  /**
   * The rows that already exist, in the order a reduction should retire them.
   *
   * Without these the intake could only ever add. It is an *add* form and
   * "Modifier le résultat" opens it, so reopening a faithfully prefilled form
   * and pressing save wrote a second cohort: eight frozen eggs became sixteen.
   * A row named here is skipped rather than written — the intake does not
   * update, because it writes the whole record positionally and every field it
   * does not read back would be overwritten.
   *
   * Rows the intake may not touch come first, so a reduction retires the tail
   * it is allowed to retire. Within the rest the order follows the maturity
   * buckets the create loop labels by position, so a cohort that grows keeps
   * labelling the way it did.
   */
  specimenIds?: string[];
  /**
   * Which of `specimenIds` a reduction may retire, and the placement to close.
   *
   * A row she has thawed, used, reserved or discarded still occupies its place
   * in `specimenIds`, so the intake never re-creates it — but it is not hers to
   * retire from here, so it gets no entry.
   */
  specimenRefs?: Record<string, SavedSpecimenRef>;
  /**
   * How many rows this cycle holds, spent ones included.
   *
   * `specimenIds` lists only the rows the intake may act on, so it cannot tell
   * "no cohort was ever saved" from "the whole cohort has been thawed". The
   * first should reopen on the cycle's step data; the second must not, or the
   * save writes a second cohort beside material she has already used.
   */
  savedRowTotal?: number;
  /**
   * The whole form, as it was saved.
   *
   * The intake writes every field at once, so it may only be allowed to save an
   * existing row if it reopens faithful to what it wrote — a field it cannot
   * read back is a field a save overwrites with a blank. Two review rounds
   * found thirteen defects of that one shape.
   *
   * `buildNotesString` already serialises the entire form onto the specimen's
   * `notes`, and `parseNotesString` already reads it back — the vault's detail
   * modal and section both do. Reading it here is what makes the reopened form
   * true, rather than teaching thirty fields to round-trip one at a time.
   * Columns win over it wherever both speak: the specimen's own `collectedAt`
   * and its storage episode are the record, the notes are the form's copy.
   */
  savedNotes?: ParsedNotesData;
  retrievalDate?: string;
  freezeDate?: string;
  clinicName?: string;
  /**
   * "stored", when a row still holds an active frozen placement.
   *
   * The one field that decides whether the whole storage block is recorded at
   * all — `useCreateSpecimen` builds a placement only for
   * `storageState === "stored"` — and the one the picker resets to null on
   * every reopen. Left out, the form came back with facility, tank and dates
   * all filled in as if remembered, saved without a placement, and wrote the
   * rows as `processed`: invisible to the vault, exactly the silent drop this
   * was meant to end.
   */
  storageState?: "stored";
  storageLocation?: string;
  storageCity?: string;
  storageCountry?: string;
  storageCountryName?: string;
  storageLocationType?: string;
};

/** The day alone: every date the intake holds is date-only. */
function dayOf(value: string | null | undefined): string | undefined {
  return typeof value === "string" && value.trim()
    ? value.slice(0, 10)
    : undefined;
}

/**
 * The location a specimen is in now — the same test the vault applies, so the
 * form and the vault can never disagree about which one is current.
 */
function activeFrozenLocation(
  locations: StorageLocation[] | undefined,
): StorageLocation | undefined {
  return locations?.find(
    (location) => location.isActive && location.storageType === "frozen",
  );
}

/**
 * The inverse of `parseTankRef`, to the extent the parse was lossless.
 *
 * `parseTankRef` reads "Tank 4, Canister B" into structured fields and drops
 * anything it could not structure into the location's meta notes, so both
 * halves have to be offered back. Labels match the ones it parses, which is
 * what lets the value survive a second round trip.
 */
function formatTankRef(location: StorageLocation): string | undefined {
  const parts = [
    location.tankNumber && `Tank ${location.tankNumber}`,
    location.canisterNumber && `Canister ${location.canisterNumber}`,
    location.subcanisterNumber && `Subcanister ${location.subcanisterNumber}`,
    location.caneNumber && `Cane ${location.caneNumber}`,
    location.rackNumber && `Rack ${location.rackNumber}`,
    location.strawNumber && `Straw ${location.strawNumber}`,
  ].filter((part): part is string => Boolean(part));
  if (parts.length > 0) return parts.join(", ");
  return parseStorageLocationMeta(location.notes)?.storageId || undefined;
}

/**
 * The order the intake's write loop assigns oocyte maturity in.
 *
 * `useCreateSpecimen` walks `i` from 0 to `numberFrozen` and labels by
 * position: the first `matureMII` rows `mii`, the next `immatureMI` rows `mi`,
 * the next `degenerated` rows `degenerated`, and leaves the field off the rest.
 * Rows handed back for update have to sit in those same buckets or a save
 * relabels her biology.
 */
const MATURITY_WRITE_ORDER = ["mii", "mi", "degenerated"] as const;

function maturityRank(stage: Specimen["maturityStage"]): number {
  const index = MATURITY_WRITE_ORDER.indexOf(
    stage as (typeof MATURITY_WRITE_ORDER)[number],
  );
  // An unlabelled or otherwise-staged row sits after the three the form
  // counts, which is where the write loop leaves the field off entirely.
  return index === -1 ? MATURITY_WRITE_ORDER.length : index;
}

/**
 * Only what this reader looks at.
 *
 * Narrower than `Specimen["specimenUsages"]` on purpose: it reads an event's
 * kind and the ids it must name back, nothing else, and a caller — or a test —
 * should not have to hand over eleven audit columns to say "this row has a
 * freeze event whose freezing info is `fi-1`".
 */
type IntakeSpecimenUsage = {
  id?: string | null;
  usage: string;
  freezingInfo?: { id?: string | null } | null;
  thawingInfo?: { id?: string | null } | null;
  embryoBiopsy?: { id?: string | null } | null;
};

type IntakeSpecimen = Pick<
  Specimen,
  "id" | "collectedAt" | "storageLocations" | "maturityStage"
> &
  Partial<Pick<Specimen, "status" | "notes">> & {
    specimenUsages?: IntakeSpecimenUsage[];
  };

/** Statuses that put a row beyond an intake's reach. */
const SPENT_SPECIMEN_STATUSES: ReadonlySet<string> = new Set([
  "used",
  "discarded",
  "donated",
]);

/**
 * Whether this row is still hers to correct through the intake.
 *
 * The caller feeds the unfiltered specimen list on purpose, because a row saved
 * before a placement was possible is exactly the row whose dates need
 * recovering. But a row she has since thawed, used or discarded keeps its
 * origin `cycleId` and its type, so it arrives here too — and handing it back
 * as an update target would let a plain reopen-and-save write it `stored`
 * again, with a fresh episode, putting biology she no longer has back in her
 * vault. Its dates still count towards the cohort's; its identity does not.
 *
 * Status alone is not enough: `CONSUMING_SPECIMEN_USAGES` is what the rest of
 * this package tests for a row that a later cycle has claimed, and a reserved
 * or fertilized row can still read as `stored`.
 */
function isCorrectableRow(specimen: IntakeSpecimen): boolean {
  if (specimen.status && SPENT_SPECIMEN_STATUSES.has(specimen.status)) {
    return false;
  }
  return !specimen.specimenUsages?.some((usage) =>
    CONSUMING_SPECIMEN_USAGES.has(usage.usage),
  );
}

/**
 * The rows in the order the intake will write them.
 *
 * Sorted by maturity bucket first, then by collection date and id so two
 * reopens of the same cohort produce the same order — an unstable order would
 * make each save shuffle which row is which.
 */
function orderForIntakeWrite<T extends IntakeSpecimen>(specimens: T[]): T[] {
  return [...specimens].sort((left, right) => {
    // Rows the intake may not retire sit at the head, so a smaller count eats
    // into the rows it is allowed to close rather than stalling on one it is
    // not.
    const byReach =
      Number(isCorrectableRow(left)) - Number(isCorrectableRow(right));
    if (byReach !== 0) return byReach;
    const byMaturity =
      maturityRank(left.maturityStage) - maturityRank(right.maturityStage);
    if (byMaturity !== 0) return byMaturity;
    const byDate = (left.collectedAt ?? "").localeCompare(
      right.collectedAt ?? "",
    );
    if (byDate !== 0) return byDate;
    return left.id.localeCompare(right.id);
  });
}

/**
 * Read the cycle's saved specimens back into intake fields.
 *
 * The earliest row wins for each date: a cohort is frozen as one event, and the
 * vault already picks the earliest `storedFrom` and `collectedAt` when it groups
 * them, so this agrees with what the vault shows rather than picking a
 * different row.
 */
export function buildIntakePrefillFromSpecimens(
  specimens: IntakeSpecimen[],
): SpecimenIntakePrefill {
  const prefill: SpecimenIntakePrefill = {};
  const ordered = orderForIntakeWrite(specimens);
  const specimenIds: string[] = [];
  const specimenRefs: Record<string, SavedSpecimenRef> = {};
  let savedRowTotal = 0;

  for (const specimen of ordered) {
    const correctable = isCorrectableRow(specimen);
    savedRowTotal += 1;
    /*
     * Only rows the intake may act on are handed back at all.
     *
     * They were once listed regardless, so a spent row would "hold its place"
     * and never be re-created. But the write loop pairs `existingIds[i]` with
     * the row it writes and never asked whether that row was hers to touch —
     * only the retiring did — so a listed thawed row was updated back to
     * `stored`, and, sorted to the head, relabelled by position as well.
     *
     * Leaving them out is safer and simpler: the count reopens as the number of
     * rows still frozen, which is what the vault shows, so the indices line up
     * with the rows and nothing beyond them is touched. `savedRowTotal` records
     * that rows do exist, so a cohort entirely spent is not mistaken for a
     * cohort never saved.
     */
    if (correctable) {
      specimenIds.push(specimen.id);
      const usageIds: NonNullable<SavedSpecimenRef["usageIds"]> = {};
      for (const usage of specimen.specimenUsages ?? []) {
        if (!usage.id) continue;
        if (
          usage.usage !== "freeze" &&
          usage.usage !== "thaw" &&
          usage.usage !== "biopsy"
        ) {
          continue;
        }
        // The nested record too: naming the usage and not its detail makes the
        // server insert a second detail against a unique constraint.
        const detailId =
          usage.usage === "freeze"
            ? usage.freezingInfo?.id
            : usage.usage === "thaw"
              ? usage.thawingInfo?.id
              : usage.embryoBiopsy?.id;
        usageIds[usage.usage] ??= {
          id: usage.id,
          ...(detailId ? { detailId } : {}),
        };
      }
      specimenRefs[specimen.id] =
        Object.keys(usageIds).length > 0 ? { usageIds } : {};
    }
    /*
     * The first row the intake may act on wins.
     *
     * They are written from one form, so a cohort's copies agree. Where they do
     * not, this deliberately prefers a row still in her vault over a spent one,
     * whose notes the CryoVault's detail modal may have edited on its own after
     * the freeze.
     */
    if (!prefill.savedNotes && specimen.notes?.trim()) {
      prefill.savedNotes = parseNotesString(specimen.notes);
    }

    const collectedAt = dayOf(specimen.collectedAt);
    if (
      collectedAt &&
      (!prefill.retrievalDate || collectedAt < prefill.retrievalDate)
    ) {
      prefill.retrievalDate = collectedAt;
    }

    const location = activeFrozenLocation(specimen.storageLocations);
    if (!location) continue;

    const storedFrom = dayOf(location.storedFrom);
    /*
     * A freeze date is only reported when the record actually holds one.
     *
     * The write falls back: `storedFrom` is `freezeDate || retrievalDate` and
     * `collectedAt` falls back to the same value, so a specimen saved with only
     * one of the two dates carries that one date in both fields — and nothing
     * records which box she typed it in. Reading `storedFrom` back as a freeze
     * date regardless put a date she never gave in front of her, and
     * `handleRecordEggRetrievalOutcome` then wrote it into the cycle's freezing
     * step as her own.
     *
     * So the two are only reported apart when they ARE apart. Equal means one
     * date is known and its meaning is not: it is offered as the retrieval
     * date, the field the cycle's phase genuinely records, and the freeze date
     * stays empty — which is what it was before any of this, and honest. The
     * field is optional and says so.
     */
    if (
      storedFrom &&
      storedFrom !== collectedAt &&
      (!prefill.freezeDate || storedFrom < prefill.freezeDate)
    ) {
      prefill.freezeDate = storedFrom;
    }
    // An active frozen placement *is* the stored state, so say so rather than
    // making her re-answer a question her own record already answers.
    prefill.storageState = "stored";
    if (!prefill.clinicName && location.facility?.trim()) {
      prefill.clinicName = location.facility.trim();
    }
    if (!prefill.storageLocation) {
      prefill.storageLocation = formatTankRef(location);
    }
    const meta = parseStorageLocationMeta(location.notes);
    if (!prefill.storageCity && meta?.location) {
      prefill.storageCity = meta.location;
    }
    if (!prefill.storageCountryName && meta?.countryName) {
      prefill.storageCountryName = meta.countryName;
    }
    /*
     * The code as well as the display name. `buildPrefillCycle` otherwise
     * seeds `country` from the device default and a re-save overwrites it —
     * material in France reopened on a US device came back `country: "US"`
     * with `countryName: "France"`, and `country` is what the detail sheets
     * render beside the facility.
     */
    if (!prefill.storageCountry && meta?.country) {
      prefill.storageCountry = meta.country;
    }
    if (!prefill.storageLocationType && meta?.facilityType) {
      prefill.storageLocationType = meta.facilityType;
    }
    if (correctable && location.id) {
      specimenRefs[specimen.id] = {
        ...specimenRefs[specimen.id],
        storageLocationId: location.id,
      };
    }
  }

  if (savedRowTotal > 0) prefill.savedRowTotal = savedRowTotal;
  if (specimenIds.length > 0) prefill.specimenIds = specimenIds;
  if (Object.keys(specimenRefs).length > 0) {
    prefill.specimenRefs = specimenRefs;
  }

  return prefill;
}
